import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import {
  loginUser,
  sendForgotPasswordOTP,
  verifyOTP,
  resetPassword,
  performSSOLogin,
  resendLoginOTP,
  clearError,
  setAuthStep,
  logout,
  initializeFromStorage,
  setSSOConfig,
} from '../store/authSlice';
import type { LoginFormData, ForgotPasswordFormData, OTPFormData, ResetPasswordFormData } from '../types/auth';
import {
  validateEmail as validateEmailUtil,
  validateOTP as validateOTPUtil,
  validatePasswordConfirmation as validatePasswordConfirmationUtil,
} from '../utils/validators';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const authState = useAppSelector((state) => state.auth);

  // Initialize auth state from session storage (Angular compatibility)
  const initializeAuth = useCallback(() => {
    dispatch(initializeFromStorage());
  }, [dispatch]);

  // Login functionality (same as Angular)
  const login = useCallback(async (formData: LoginFormData) => {
    const result = await dispatch(loginUser({
      email: formData.email,
      password: formData.password,
    }));
    
    return result;
  }, [dispatch]);

  // Forgot password functionality (same as Angular)
  const sendForgotPassword = useCallback(async (formData: ForgotPasswordFormData) => {
    const result = await dispatch(sendForgotPasswordOTP(formData.email));
    return result;
  }, [dispatch]);

  // OTP verification (same as Angular)
  const verifyOTPCode = useCallback(async (formData: OTPFormData) => {
    if (!authState.otpReferenceId) {
      throw new Error('No OTP reference ID found');
    }
    
    const result = await dispatch(verifyOTP({
      otp: formData.otp,
      reference_id: authState.otpReferenceId,
    }));
    
    return result;
  }, [dispatch, authState.otpReferenceId]);

  // Password reset functionality (same as Angular)
  const resetUserPassword = useCallback(async (formData: ResetPasswordFormData, token?: string) => {
    const resetToken = token || authState.otpReferenceId;
    
    if (!resetToken) {
      throw new Error('No reset token found');
    }
    
    const result = await dispatch(resetPassword({
      email: authState.user?.email || '',
      newpassword: formData.newPassword,
      oldpassword: '', // Empty for password reset via OTP
    }));
    
    return result;
  }, [dispatch, authState.otpReferenceId]);

  // SSO login functionality (same as Angular)
  const loginWithSSO = useCallback(async (ssoData: { userId: string; password: string }) => {
    const result = await dispatch(performSSOLogin(ssoData));
    return result;
  }, [dispatch]);

  // Resend Login OTP functionality (matching Angular resend OTP)
  const resendOTP = useCallback(async () => {
    if (!authState.tempCredentials) {
      throw new Error('No credentials found for resending OTP');
    }

    const result = await dispatch(resendLoginOTP(authState.tempCredentials));
    return result;
  }, [dispatch, authState.tempCredentials]);

  // Navigation helpers (matching Angular auth steps)
  const goToLogin = useCallback(() => {
    dispatch(setAuthStep('login'));
  }, [dispatch]);

  const goToForgotPassword = useCallback(() => {
    dispatch(setAuthStep('forgot-password'));
  }, [dispatch]);

  const goToMFA = useCallback(() => {
    dispatch(setAuthStep('mfa'));
  }, [dispatch]);

  const goToPasswordReset = useCallback(() => {
    dispatch(setAuthStep('reset-password'));
  }, [dispatch]);

  const goToSignup = useCallback(() => {
    dispatch(setAuthStep('signup'));
  }, [dispatch]);

  // Error management
  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Logout functionality
  const logoutUser = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  // Clear auth state (alias for logout)
  const clearAuthState = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  // SSO configuration (matching Angular environment settings)
  const configureSSOProviders = useCallback((providers: string[], canShow: boolean) => {
    dispatch(setSSOConfig({ providers, canShow }));
  }, [dispatch]);

  // Validation helpers (matching Angular form validation)
  const validateEmail = useCallback((email: string): string | undefined => {
    return validateEmailUtil(email);
  }, []);

  const validatePassword = useCallback((password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (password.length < authState.passwordPolicy.minLength) {
      return `Password must be at least ${authState.passwordPolicy.minLength} characters`;
    }
    if (password.length > authState.passwordPolicy.maxLength) {
      return `Password must be no more than ${authState.passwordPolicy.maxLength} characters`;
    }
    return undefined;
  }, [authState.passwordPolicy]);

  const validateOTP = useCallback((otp: string): string | undefined => {
    return validateOTPUtil(otp);
  }, []);

  const validatePasswordConfirmation = useCallback((password: string, confirmPassword: string): string | undefined => {
    return validatePasswordConfirmationUtil(password, confirmPassword);
  }, []);

  return {
    // State
    ...authState,
    email: authState.user?.email || '', // Extract email for components

    // Actions
    initializeAuth,
    login,
    sendForgotPassword,
    verifyOTP: verifyOTPCode,
    resetPassword: resetUserPassword,
    performSSOLogin: loginWithSSO,
    resendOTP,
    logoutUser,
    clearAuthState,

    // Navigation
    goToLogin,
    goToForgotPassword,
    goToMFA,
    goToPasswordReset,
    goToSignup,

    // Utilities
    clearAuthError,
    configureSSOProviders,

    // Validation
    validateEmail,
    validatePassword,
    validateOTP,
    validatePasswordConfirmation,
  };
};